import { Alert, Box, Button, Paper } from "@mui/material";
import CustomTable, {
  type CustomTableColumn,
} from "components/common/CustomTable";
import RowActions from "components/common/RowActions";
import DashboardLayout from "components/layouts/DashboardLayout";
import { adminApi } from "apis";
import { usePatients } from "hooks/usePatients";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router";
import type { Patient } from "types";
import {
  calculateBirthDateRange,
  formatBirthDate,
  formatPatientName,
} from "utils";
import { EPath } from "constants/path";

import PatientFilters from "./PatientFilters";
import CancelPatientRequestModal from "./CancelPatientRequestModal";
import PatientSearch from "./PatientSearch";
import PatientRequestModal from "./PatientRequestModal";
import RequestPatientConfirmModal from "./RequestPatientConfirmModal";
import {
  PatientsPaginationRequestDtoGenderEnum,
  PatientsPaginationRequestDtoSortByEnum,
  PatientsPaginationRequestDtoSortOrderEnum,
  PatientsPaginationRequestDtoStatusEnum,
} from "libs/cellera-sdk";
import { useCelleraTheme } from "theme/useCelleraTheme";

interface PatientFiltersState {
  search: string;
  gender: PatientsPaginationRequestDtoGenderEnum[];
  status: PatientsPaginationRequestDtoStatusEnum[];
  ageFrom: number | null;
  ageTo: number | null;
  sortBy: string;
  sortOrder: "asc" | "desc";
  page: number;
  pageSize: number;
}

const DEFAULT_PAGE_SIZE = 10;

const PatientManagement: React.FC = () => {
  const { colors } = useCelleraTheme();
  const navigate = useNavigate();

  const [filters, setFilters] = useState<PatientFiltersState>({
    search: "",
    gender: [],
    status: [
      PatientsPaginationRequestDtoStatusEnum.ActiveSharing,
      PatientsPaginationRequestDtoStatusEnum.Requesting,
    ],
    ageFrom: null,
    ageTo: null,
    sortBy: "requestedDate",
    sortOrder: "desc",
    page: 0,
    pageSize: DEFAULT_PAGE_SIZE,
  });

  const [debouncedSearch, setDebouncedSearch] = useState(filters.search);
  const [isRequestModalOpen, setIsRequestModalOpen] = useState(false);
  const [isCancelModalOpen, setIsCancelModalOpen] = useState(false);
  const [isRequestConfirmModalOpen, setIsRequestConfirmModalOpen] =
    useState(false);
  const [patientPendingCancellation, setPatientPendingCancellation] =
    useState<Patient | null>(null);
  const [patientPendingRequest, setPatientPendingRequest] =
    useState<Patient | null>(null);

  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(filters.search);
    }, 300);

    return () => clearTimeout(timer);
  }, [filters.search]);

  const birthDateRange = useMemo(
    () => calculateBirthDateRange(filters.ageFrom, filters.ageTo),
    [filters.ageFrom, filters.ageTo],
  );

  const { patients, totalCount, loading, error, refetch } = usePatients({
    page: filters.page,
    pageSize: filters.pageSize,
    search: debouncedSearch || undefined,
    gender: filters.gender.length > 0 ? filters.gender : undefined,
    status: filters.status.length > 0 ? filters.status : undefined,
    birthDateFrom: birthDateRange.birthDateFrom,
    birthDateTo: birthDateRange.birthDateTo,
    sortBy: filters.sortBy as PatientsPaginationRequestDtoSortByEnum,
    sortOrder:
      filters.sortOrder === "asc"
        ? PatientsPaginationRequestDtoSortOrderEnum.Asc
        : PatientsPaginationRequestDtoSortOrderEnum.Desc,
  });

  const handleOpenRequestModal = useCallback(() => {
    setIsRequestModalOpen(true);
  }, []);

  const handleCloseRequestModal = useCallback(() => {
    setIsRequestModalOpen(false);
  }, []);

  const handleOpenCancelModal = useCallback((patient: Patient) => {
    setPatientPendingCancellation(patient);
    setIsCancelModalOpen(true);
  }, []);

  const handleCloseCancelModal = useCallback(() => {
    setIsCancelModalOpen(false);
    setPatientPendingCancellation(null);
  }, []);

  const handleOpenRequestConfirmModal = useCallback((patient: Patient) => {
    setPatientPendingRequest(patient);
    setIsRequestConfirmModalOpen(true);
  }, []);

  const handleCloseRequestConfirmModal = useCallback(() => {
    setIsRequestConfirmModalOpen(false);
    setPatientPendingRequest(null);
  }, []);

  const handlePatientRequestSubmit = useCallback(
    async (email: string) => {
      try {
        await adminApi.requestPatientDataSharingForAdmin({
          requestPatientDataSharingDto: { email },
        });
        refetch();
      } catch (error) {
        if (error instanceof Error) {
          throw error;
        }
        throw new Error("Failed to send request. Please try again.");
      }
    },
    [refetch],
  );

  const handleCancelRequestSubmit = useCallback(async () => {
    if (!patientPendingCancellation?.patientId) {
      throw new Error(
        "Unable to determine the patient for cancellation. Please try again.",
      );
    }

    try {
      await adminApi.cancelPatientDataSharingForAdmin({
        cancelDataSharingRequestsDto: {
          patientIds: [patientPendingCancellation.patientId],
        },
      });
      refetch();
    } catch (error) {
      if (error instanceof Error) {
        throw error;
      }
      throw new Error("Failed to cancel request. Please try again.");
    }
  }, [patientPendingCancellation, refetch]);

  const handleRequestAgainSubmit = useCallback(async () => {
    if (!patientPendingRequest?.email) {
      throw new Error(
        "Patient email is unavailable. Cannot send a data sharing request.",
      );
    }

    try {
      await adminApi.requestPatientDataSharingForAdmin({
        requestPatientDataSharingDto: {
          email: patientPendingRequest.email,
        },
      });
      refetch();
    } catch (error) {
      if (error instanceof Error) {
        throw error;
      }
      throw new Error("Failed to send request. Please try again.");
    }
  }, [patientPendingRequest, refetch]);

  const handleSearchChange = useCallback((search: string) => {
    setFilters((prev) => ({ ...prev, search, page: 0 }));
  }, []);

  const handleGenderChange = useCallback(
    (genderValues: PatientsPaginationRequestDtoGenderEnum[]) => {
      setFilters((prev) => ({ ...prev, gender: genderValues, page: 0 }));
    },
    [],
  );

  const handleStatusChange = useCallback(
    (statusValues: PatientsPaginationRequestDtoStatusEnum[]) => {
      setFilters((prev) => ({ ...prev, status: statusValues, page: 0 }));
    },
    [],
  );

  const handleAgeRangeChange = useCallback(
    (ageFrom: number | null, ageTo: number | null) => {
      setFilters((prev) => ({ ...prev, ageFrom, ageTo, page: 0 }));
    },
    [],
  );

  const getStatusColor = useCallback(
    (status?: string) => {
      switch (status?.toLowerCase()) {
        case PatientsPaginationRequestDtoStatusEnum.ActiveSharing:
          return {
            border: `1px solid ${colors.success[700]}`,
            backgroundColor: colors.success[100],
            color: colors.success[700],
          };
        case PatientsPaginationRequestDtoStatusEnum.Revoked:
          return {
            border: `1px solid ${colors.error[700]}`,
            backgroundColor: colors.error[100],
            color: colors.error[700],
          };
        case PatientsPaginationRequestDtoStatusEnum.Requesting:
          return {
            border: `1px solid ${colors.warning[700]}`,
            backgroundColor: colors.warning[100],
            color: colors.warning[700],
          };
        default:
          return {
            p: 0,
            fontSize: 14,
          };
      }
    },
    [colors],
  );

  const getStatusLabel = useCallback((status?: string) => {
    switch (status?.toLowerCase()) {
      case PatientsPaginationRequestDtoStatusEnum.ActiveSharing:
        return "Active Sharing";
      case PatientsPaginationRequestDtoStatusEnum.Revoked:
        return "Revoked";
      case PatientsPaginationRequestDtoStatusEnum.Requesting:
        return "Requesting";
      default:
        return "N/A";
    }
  }, []);

  const handleViewPatient = useCallback(
    (patient: Patient) => {
      navigate(EPath.PATIENT_DETAIL.replace(":patientId", patient.patientId));
    },
    [navigate],
  );

  const columns: CustomTableColumn<Patient>[] = useMemo(
    () => [
      {
        id: "email",
        label: "Email",
        sortable: true,
        sortAccessor: (row: Patient) => row.email ?? "",
        getValue: (row: Patient) => row.email ?? "N/A",
      },
      {
        id: "fullName",
        label: "Full Name",
        sortable: true,
        sortAccessor: (row: Patient) => formatPatientName(row),
        getValue: (row: Patient) => formatPatientName(row),
      },
      {
        id: "gender",
        label: "Gender",
        getValue: (row: Patient) => row.gender ?? "N/A",
      },
      {
        id: "birthDate",
        label: "Birthday",
        sortable: true,
        sortAccessor: (row: Patient) =>
          row.birthDate ? new Date(row.birthDate) : null,
        getValue: (row: Patient) =>
          row.birthDate ? formatBirthDate(row.birthDate) : "N/A",
      },
      {
        id: "phone",
        label: "Phone",
        sortable: true,
        sortAccessor: (row: Patient) => row.phone ?? "",
        getValue: (row: Patient) => row.phone ?? "N/A",
      },
      {
        id: "status",
        label: "Status",
        getValue: (row: Patient) => (
          <Box
            sx={{
              display: "inline-flex",
              alignItems: "center",
              px: 1.5,
              py: 0.5,
              borderRadius: 0.5,
              fontSize: 12,
              fontWeight: 600,
              ...getStatusColor(row.status),
            }}
          >
            {getStatusLabel(row.status)}
          </Box>
        ),
      },
      {
        id: "actions",
        label: "",
        align: "center",
        getValue: (row: Patient) => (
          <RowActions
            onViewDetails={() => {
              handleViewPatient(row);
            }}
            onCancelRequest={() => {
              handleOpenCancelModal(row);
            }}
            showCancelRequest={
              row.status === PatientsPaginationRequestDtoStatusEnum.Requesting
            }
            onRequest={() => {
              handleOpenRequestConfirmModal(row);
            }}
            showRequest={
              row.status === PatientsPaginationRequestDtoStatusEnum.Revoked
            }
            disabledRequest={!row.email}
          />
        ),
        width: "5%",
      },
    ],
    [
      getStatusColor,
      getStatusLabel,
      handleOpenCancelModal,
      handleOpenRequestConfirmModal,
      handleViewPatient,
    ],
  );

  const handlePageChange = useCallback((_: unknown, newPage: number) => {
    setFilters((prev) => ({ ...prev, page: newPage }));
  }, []);

  const handleRowsPerPageChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      setFilters((prev) => ({
        ...prev,
        pageSize: Number.parseInt(event.target.value, 10),
        page: 0,
      }));
    },
    [],
  );

  return (
    <DashboardLayout
      title="Patient Management"
      headerActions={
        <Button
          variant="contained"
          onClick={handleOpenRequestModal}
          className="rounded-full px-6 py-2 font-inter text-white transition-all duration-200 hover:shadow-lg"
          sx={{
            textTransform: "none",
            fontWeight: 600,
            backgroundColor: colors.primary.main,
            "&:hover": {
              backgroundColor: colors.primary.dark,
            },
          }}
        >
          Request access to a new patient profile
        </Button>
      }
    >
      <Box className="flex flex-col h-full">
        <Paper
          elevation={0}
          sx={{
            border: `1px solid ${colors.border.light}`,
            borderRadius: 1,
            p: 2,
          }}
        >
          {error && (
            <Alert
              severity="error"
              onClose={() => {
                refetch();
              }}
              sx={{ mb: 2 }}
            >
              {error}
            </Alert>
          )}

          <Box className="mb-4">
            <Box className="grid grid-cols-2 gap-4 items-stretch">
              <PatientSearch
                value={filters.search}
                onChange={handleSearchChange}
              />
              <Box className="w-full flex justify-end">
                <PatientFilters
                  gender={filters.gender}
                  status={filters.status}
                  ageFrom={filters.ageFrom}
                  ageTo={filters.ageTo}
                  onGenderChange={handleGenderChange}
                  onStatusChange={handleStatusChange}
                  onAgeRangeChange={handleAgeRangeChange}
                />
              </Box>
            </Box>
          </Box>

          <Box className="flex-1">
            <CustomTable<Patient>
              data={patients}
              columns={columns}
              totalCount={totalCount}
              page={filters.page}
              rowsPerPage={filters.pageSize}
              onPageChange={handlePageChange}
              onRowsPerPageChange={handleRowsPerPageChange}
              isLoading={loading}
              loadingMessage="Loading patients..."
              tableContainerSx={{
                height: "calc(100vh - 250px)",
              }}
            />
          </Box>
        </Paper>
        <PatientRequestModal
          open={isRequestModalOpen}
          onClose={handleCloseRequestModal}
          onSubmit={handlePatientRequestSubmit}
        />
        <CancelPatientRequestModal
          open={isCancelModalOpen}
          onClose={handleCloseCancelModal}
          onSubmit={handleCancelRequestSubmit}
          patientEmail={patientPendingCancellation?.email}
        />
        <RequestPatientConfirmModal
          open={isRequestConfirmModalOpen}
          onClose={handleCloseRequestConfirmModal}
          onSubmit={handleRequestAgainSubmit}
          patientEmail={patientPendingRequest?.email}
        />
      </Box>
    </DashboardLayout>
  );
};

export default PatientManagement;
